import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';

import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

import { DateFormat, DateLlFormat, NdviDataSmoothingFactor } from '../constants/data';
import { ChartDeepOptions } from '../components/Charts/constants';

dayjs.extend(isToday);

/** ==== Types ==== */
type Nullable<T> = T | null | undefined;

type AirMetric = {
  datetime: string;
  air_moisture: number | null;
  air_temperature: number | null;
  dew_fall_intensity: number;
};

type NdviGddMetric = {
  date: string;
  'ndvi.p5'?: number | null;
  'ndvi.p95'?: number | null;
  'ndvi.p50': number;
  'gdd.value': number | null;
  'bbch_ai.value'?: number | null;
  'bbch_ai.description'?: string;
  'crop_mask_ratio.value': number | null;
};

type NdviMetaStats = {
  p5?: number | null;
  p50?: number | null;
  p95?: number | null;
};

type VegetationMetric = {
  date: string;
  photos?: {
    ndvi?: Array<{ meta?: { crop_ndvi?: NdviMetaStats; soil_ndvi?: NdviMetaStats } }>;
  };
};

type SoilMetric = {
  datetime: string;
  soil_moisture: number[];
  soil_temperature: number[];
  soil_salinity: number[];
};

type ValuePoint = { date: string; value: number | null };
type AreaPoint = { date: string; value: number; area: number[] };
type BbchPoint = { date: string; value: number; text: string };

type AirMetricsResult = {
  temperature: AreaPoint[] | null;
  moisture: ValuePoint[] | null;
  dewPoint: ValuePoint[] | null;
};

type NdviGddResult = {
  ndvi: AreaPoint[];
  gdd: ValuePoint[];
  bbch: BbchPoint[];
  leafArea: ValuePoint[];
};

type VegetationResult = {
  leafNdvi: AreaPoint[];
  soilNdvi: AreaPoint[];
};

type ChartsMetricsArgs<T> = {
  metrics: Nullable<T[]>;
  metricsCompared?: Nullable<T[]>;
  period: number;
  sensorCompared?: unknown;
};

/** ==== Metrics ==== */
/**
 * Calculate min, mean, max temperature for 1 date
 */
const getTemperature = (
  data: Array<number | null | undefined>,
): { min: number | null; max: number | null; mean: number | null } => {
  const result = [...data]
    .filter((value): value is number => value !== null && value !== undefined)
    .sort((a, b) => a - b);

  if (!result.length) {
    return { min: null, max: null, mean: null };
  }

  const sum = result.reduce((a, b) => {
    return a + b;
  }, 0);

  const mean = Math.round(sum / result.length);
  const min = result[0];
  const max = result[result.length - 1];

  return { min, max, mean };
};

const getMoisture = (data: Array<number | null | undefined>): { mean: number | null } => {
  const result = [...data]
    .filter((value): value is number => value !== null && value !== undefined)
    .sort((a, b) => a - b);
  // const half = Math.floor(result.length / 2);

  if (!result.length) {
    return { mean: null };
  }

  const sum = result.reduce(function (a, b) {
    return a + b;
  }, 0);

  // const minMoisture = Math.min(...result);
  const meanMoisture = Math.round(sum / result.length);
  // const meanMoisture = result.length % 2 ? result[half] : (result[half - 1] + result[half]) / 2;

  // const dewPoint =
  //   minTemperature -
  //   (14.55 + 0.114 * minTemperature) * (1 - 0.01 * minMoisture) -
  //   Math.pow((2.5 + 0.007 * minTemperature) * (1 - 0.01 * minMoisture), 3) -
  //   (15.9 + 0.117 * minTemperature) * Math.pow(1 - 0.01 * minMoisture, 14);

  // const dewPoint = calculateDewPoint(minTemperature, minMoisture)

  return { mean: meanMoisture };
  // return { mean: meanMoisture, dew: dewPoint };
};

const getDewProbabilityAvg = (data: number[], date: string): number => {
  // let time = 0;
  // let sum = data.reduce(function(a, b) {
  //   return a + b;
  // }, 0);
  // const meanTime = sum * 60 / data.length;
  // data.map((item) => {
  //   time = time + item * 60;
  // });

  // Evgeny, [22.05.24 15:47]
  // seconds_elapsed = min(max((datetime.now() - day_start).seconds, 1), 24 * 60 * 60)
  // avg_period = seconds_elapsed / metrics_count
  // dew_fall_duration = int(sum([value * avg_period for value in dew_fall_intensity_values]))
  //
  // время текущее я беру по UTC
  // metrics-count - это сколько измерений было за текущий день
  // seconds_elapsed - это я считаю сколько прошло текущего дня. если день в прошлом, то это будет максимум 86400 секунд, если это текущий день - то считается
  // а дальше я просто высчитываю среднюю длину периода между измерениями и по очереди умножаю на каждое доступное dew_intensity
  let secondsElapsed = 24 * 60 * 60;
  if (dayjs(date).isToday()) {
    secondsElapsed = (dayjs().valueOf() - dayjs().startOf('day').valueOf()) * 0.001;
  }
  const avgPeriod = secondsElapsed / data.length;
  let duration = 0;
  data.forEach((item) => {
    duration = duration + item * avgPeriod;
  });
  return Math.round(duration / 60);
};
// function calculateDewPoint(t, rh) {
//   const a = 17.27;
//   const b = 237.7;
//
//   const alpha = ((a * t) / (b + t)) + Math.log(rh/100.0);
//   return (b * alpha) / (a - alpha);
// }

const getAirMetrics = (data: AirMetric[] = [], period: number): AirMetricsResult => {
  if (!data.length) {
    return { temperature: null, moisture: null, dewPoint: null };
  }

  // const byHour = false; // period < ~7 days

  // Temperature
  const temperature: AreaPoint[] = [];
  const moisture: ValuePoint[] = [];
  const dewPoint: ValuePoint[] = [];

  let exit = false;

  // define the earliest date of period
  const lastDate = dayjs(data[data.length - 1].datetime);
  const minDate = lastDate.subtract(period, 'day');

  // if (!byHour) {
  // aggregate data from BE by 1 day
  // we get from BE set of values for 1 day, so we need to aggregate into 1 object
  // where key will be a date and value for the key will be an array of values
  const aggregatedData = data.reduce<
    Record<
      string,
      {
        temperatures: Array<number | null>;
        moistureArray: Array<number | null>;
        dewPointProbabilityArray: number[];
      }
    >
  >((aggregated, item) => {
    const { datetime, air_moisture, air_temperature, dew_fall_intensity } = item;

    const key = dayjs(datetime).format(DateFormat);

    if (!aggregated[key]) {
      aggregated[key] = { temperatures: [], moistureArray: [], dewPointProbabilityArray: [] };
    }

    aggregated[key].moistureArray.push(air_moisture);
    aggregated[key].dewPointProbabilityArray.push(dew_fall_intensity);
    aggregated[key].temperatures.push(air_temperature);
    return aggregated;
  }, {});

  // get all available dates
  const dates = Object.keys(aggregatedData);

  let i = dates.length - 1;

  while (!exit && i >= 0) {
    const date = dates[i];

    if (dayjs(date).isAfter(minDate, 'day')) {
      const { temperatures, moistureArray, dewPointProbabilityArray } = aggregatedData[date];

      // calculate values for min, mean, max of temperature from aggregated data
      const { min, max, mean } = getTemperature(temperatures);
      const { mean: meanMoisture } = getMoisture(moistureArray);
      const dewProbability = getDewProbabilityAvg(dewPointProbabilityArray, date);

      temperature.push({
        date: dayjs(date).format(DateLlFormat),
        area: [Number(min!.toFixed(1)), Number(max!.toFixed(1))],
        value: Number(mean),
      });
      moisture.push({
        date: dayjs(date).format(DateLlFormat),
        value: meanMoisture,
      });
      dewPoint.push({
        date: dayjs(date).format(DateLlFormat),
        value: dewProbability,
      });
    } else {
      // stop calculation and exit if period is over
      exit = true;
    }
    i--;
  }
  // }
  // else {
  //   let i = data.length - 1;
  //
  //   while (!exit && i >= 0) {
  //     if (dayjs(data[i].date).isAfter(minDate, 'day')) {
  //       temperature.push({
  //         date: dayjs(data[i].datetime).format(DateTimeLlFormat),
  //         value: Number(data[i].air_temperature.toFixed(1)),
  //       });
  //     } else {
  //       exit = true;
  //     }
  //     i--;
  //   }
  // }
  return { temperature, moisture, dewPoint };
};

const getNdviGddMetrics = (data: NdviGddMetric[] = [], period: number): NdviGddResult | undefined => {
  if (!data || data.length === 0) return;

  // define the earliest date of period
  const lastDate = dayjs(data[data.length - 1].date);
  const minDate = lastDate.subtract(period, 'day');

  const ndvi: AreaPoint[] = [];
  const gdd: ValuePoint[] = [];
  const bbch: BbchPoint[] = [];
  const leafArea: ValuePoint[] = []; // crop_mask_ratio.value

  let exit = false;
  let i = data.length - 1;

  let isGddNull = true;

  while (!exit && i >= 0) {
    const { date } = data[i];
    const p5 = data[i]['ndvi.p5'];
    const p95 = data[i]['ndvi.p95'];

    let ndviValue = data[i]['ndvi.p50'];
    const ndviArea = (p5 || p5 === 0) && (p95 || p95 === 0) ? [data[i]['ndvi.p5'], data[i]['ndvi.p95']] : [];
    const gddValue = data[i]['gdd.value'];

    // if date from BE is after the earliest date of period
    if (dayjs(date).isAfter(minDate, 'day')) {
      if (ndvi.length) {
        const { value: lastNdviValue } = ndvi[ndvi.length - 1];
        ndviValue = lastNdviValue * (1 - NdviDataSmoothingFactor) + ndviValue * NdviDataSmoothingFactor;
      }
      ndvi.push({
        date: dayjs(date).format(DateLlFormat),
        value: ndviValue,
        area: ndviArea.length > 1 ? [Number(ndviArea[0]), Number(ndviArea[1])] : [],
      });

      if (isGddNull && gddValue !== null) {
        isGddNull = false;
      }
      gdd.push({
        date: dayjs(date).format(DateLlFormat),
        value: gddValue,
      });

      if (data[i]['bbch_ai.value']) {
        bbch.push({
          date: dayjs(date).format(DateLlFormat),
          value: data[i]['bbch_ai.value']!,
          text: data[i]['bbch_ai.description']!,
        });
      }
      const leafAreaValue = data[i]['crop_mask_ratio.value'];
      if (leafAreaValue !== null) {
        const leafAreaPercent = leafAreaValue * 100;
        leafArea.push({
          date: dayjs(date).format(DateLlFormat),
          value: Number.isInteger(leafAreaPercent) ? leafAreaPercent : Number(leafAreaPercent.toFixed(1)),
        });
      }
      // stop calculation and exit
    } else {
      exit = true;
    }
    i--;
  }

  return { ndvi, gdd: isGddNull ? [] : gdd, bbch, leafArea };
};

const getVegetationMetrics = (data: VegetationMetric[] = [], period: number): VegetationResult | undefined => {
  if (!data || data.length === 0) return;

  // define the earliest date of period
  const lastDate = dayjs(data[data.length - 1].date);
  const minDate = lastDate.subtract(period, 'day');

  const leafNdvi: AreaPoint[] = [];
  const soilNdvi: AreaPoint[] = [];
  let exit = false;
  let i = data.length - 1;

  while (!exit && i >= 0) {
    const { date } = data[i];

    if (dayjs(date).isAfter(minDate, 'day')) {
      const ndviMeta = data[i]?.photos?.ndvi?.[0]?.meta;
      if (ndviMeta) {
        const cropValues = ndviMeta.crop_ndvi;
        if (cropValues) {
          const leafValue = cropValues['p50'];
          const leafP5 = cropValues['p5'];
          const leafP95 = cropValues['p95'];
          const leafArea = (leafP5 || leafP5 === 0) && (leafP95 || leafP95 === 0) ? [leafP5, leafP95] : [];

          if (leafValue !== null && leafValue !== undefined) {
            leafNdvi.push({
              date: dayjs(date).format(DateLlFormat),
              value: leafValue,
              area: leafArea as number[],
            });
          }
        }
        const soilValues = ndviMeta.soil_ndvi;
        if (soilValues) {
          const soilValue = soilValues['p50'];
          const soilP5 = soilValues['p5'];
          const soilP95 = soilValues['p95'];
          const soilArea = (soilP5 || soilP5 === 0) && (soilP95 || soilP95 === 0) ? [soilP5, soilP95] : [];

          if (soilValue !== null && soilValue !== undefined) {
            soilNdvi.push({
              date: dayjs(date).format(DateLlFormat),
              value: soilValue,
              area: soilArea as number[],
            });
          }
        }
      }
    } else {
      exit = true;
    }

    i--;
  }

  return { leafNdvi, soilNdvi };
};

const MergeComparedSeries = <T extends { date: string }>(
  baseSeries: T[] = [],
  comparedSeries: Array<Record<string, unknown> & { date: string }> = [],
): T[] => {
  if (!baseSeries.length || !comparedSeries.length) return baseSeries;

  const comparedByDate = comparedSeries.reduce<Record<string, Record<string, unknown> & { date: string }>>(
    (acc, item) => {
      acc[item.date] = item;
      return acc;
    },
    {},
  );

  return baseSeries.map((item) => {
    const comparedItem = comparedByDate[item.date];
    if (!comparedItem) return item;

    const comparedFields = Object.keys(comparedItem).reduce<Record<string, unknown>>((acc, key) => {
      if (key === 'date') return acc;
      acc[`${key}Compared`] = comparedItem[key];
      return acc;
    }, {});

    return {
      ...item,
      ...comparedFields,
    };
  });
};

export const getSoilMoistureChartData = (
  metrics: Nullable<SoilMetric[]>,
): { data: Record<string, string | number | null>[]; dataByDays: Record<string, ValuePoint[]> } => {
  const data: Record<string, string | number | null>[] = [];
  const dataByDays: Record<string, ValuePoint[]> = {};

  if (!metrics || metrics.length === 0) {
    return { data, dataByDays };
  }

  metrics.forEach((item) => {
    const transformed = item.soil_moisture.reduce<Record<string, string | number | null>>(
      (acc, val, index) => {
        acc[`value${(index + 1) * 10}`] = val;
        return acc;
      },
      { date: item.datetime },
    );

    data.push(transformed);
  });

  const groupedByDay = metrics.reduce<Record<string, number[][]>>((acc, item) => {
    const date = dayjs(item.datetime).format(DateLlFormat);

    if (!acc[date]) {
      acc[date] = [];
    }

    acc[date].push(item.soil_moisture);
    return acc;
  }, {});

  Object.entries(groupedByDay).forEach(([date, dayValues]) => {
    const depthCount = dayValues[0]?.length ?? 0;

    for (let index = 0; index < depthCount; index++) {
      const depth = (index + 1) * 10;
      const valuesByHours = dayValues
        .map((values) => values[index])
        .filter((value) => value !== null && value !== undefined);
      const avgValue = valuesByHours.length
        ? Math.round((valuesByHours.reduce((sum, value) => sum + value, 0) / valuesByHours.length) * 100) / 100
        : null;

      const key = `soilHumidity_${depth}`;
      if (!dataByDays[key]) {
        dataByDays[key] = [];
      }
      dataByDays[key].push({ date, value: avgValue });
    }
  });

  return { data, dataByDays };
};

export const getSoilTemperatureChartData = (
  metrics: Nullable<SoilMetric[]>,
): { data: Record<string, string | number | null>[]; dataByDays: Record<string, ValuePoint[]> } => {
  const data: Record<string, string | number | null>[] = [];
  const dataByDays: Record<string, ValuePoint[]> = {};

  if (metrics && metrics.length > 0) {
    metrics.forEach((item) => {
      const transformed = item.soil_temperature.reduce<Record<string, string | number | null>>(
        (acc, val, index) => {
          acc[`value${(index + 1) * 10}`] = Math.round(val * 100) / 100;
          return acc;
        },
        { date: item.datetime },
      );

      data.push(transformed);
    });

    const groupedByDay = metrics.reduce<Record<string, number[][]>>((acc, item) => {
      const date = dayjs(item.datetime).format(DateLlFormat);

      if (!acc[date]) {
        acc[date] = [];
      }

      acc[date].push(item.soil_temperature);
      return acc;
    }, {});

    Object.entries(groupedByDay).forEach(([date, dayValues]) => {
      const depthCount = dayValues[0]?.length ?? 0;

      for (let index = 0; index < depthCount; index++) {
        const depth = (index + 1) * 10;
        const valuesByHours = dayValues
          .map((values) => values[index])
          .filter((value) => value !== null && value !== undefined);
        const avgValue = valuesByHours.length
          ? Math.round((valuesByHours.reduce((sum, value) => sum + value, 0) / valuesByHours.length) * 100) / 100
          : null;

        const key = `soilTemperature_${depth}`;
        if (!dataByDays[key]) {
          dataByDays[key] = [];
        }
        dataByDays[key].push({ date, value: avgValue });
      }
    });
  }

  return { data, dataByDays };
};

export const getSoilSalinityChartData = (
  metrics: Nullable<SoilMetric[]>,
): {
  data: Record<string, string | number | null>[];
  isNoData: boolean;
  count: number;
  dataByDays: Record<string, ValuePoint[]>;
} => {
  const data: Record<string, string | number | null>[] = [];
  const dataByDays: Record<string, ValuePoint[]> = {};

  let count = 0;
  let isNoData = true;

  if (metrics && metrics.length > 0 && metrics[0].soil_salinity) {
    count = metrics[0].soil_salinity.length;
    metrics.forEach((item) => {
      const transformed = item.soil_salinity.reduce<Record<string, string | number | null>>(
        (acc, val, index) => {
          if (isNoData && val !== null && val !== undefined) {
            isNoData = false;
          }

          acc[`value${(index + 1) * 10}`] = Math.round(val * 100) / 100;
          return acc;
        },
        { date: item.datetime },
      );

      data.push(transformed);
    });

    if (!isNoData) {
      const groupedByDay = metrics.reduce<Record<string, number[][]>>((acc, item) => {
        const date = dayjs(item.datetime).format(DateLlFormat);

        if (!acc[date]) {
          acc[date] = [];
        }

        acc[date].push(item.soil_salinity);
        return acc;
      }, {});

      Object.entries(groupedByDay).forEach(([date, dayValues]) => {
        const depthCount = dayValues[0]?.length ?? 0;

        for (let index = 0; index < depthCount; index++) {
          const depth = (index + 1) * 10;
          const valuesByHours = dayValues
            .map((values) => values[index])
            .filter((value) => value !== null && value !== undefined);
          const avgValue = valuesByHours.length
            ? Math.round((valuesByHours.reduce((sum, value) => sum + value, 0) / valuesByHours.length) * 100) / 100
            : null;

          const key = `salinity_${depth}`;
          if (!dataByDays[key]) {
            dataByDays[key] = [];
          }
          dataByDays[key].push({ date, value: avgValue });
        }
      });
    }
  }

  return { data: !isNoData ? data : [], isNoData, count, dataByDays };
};

export const getRainChartData = (metrics: Nullable<SoilMetric[]>): ValuePoint[] => {
  const data: { datetime: string; value: number }[] = [];
  const result: ValuePoint[] = [];

  if (metrics && metrics.length > 0) {
    metrics.forEach((item) => {
      data.push({
        datetime: item.datetime,
        value: item.soil_moisture[0],
      });
    });

    for (let i = 0; i < data.length - 1; i++) {
      const t0 = new Date(data[i].datetime);
      // const t1 = new Date(data[i + 1].datetime);
      const v0 = data[i].value;
      const v1 = data[i + 1].value;

      let rate = (v1 - v0) / 3; // %/h

      // Берем только положительное значение
      rate = rate > 0 ? rate : 0;

      result.push({
        date: t0.toISOString(),
        value: parseFloat(rate.toFixed(3)),
      });

      // Добавляем производную для каждого часа между t0 и t1
      // for (let h = 1; h <= 3; h++) {
      //   const t = new Date(t0.getTime() + h * 60 * 60 * 1000); // t0 + h часов
      //   result.push({
      //     datetime: t.toISOString(),
      //     rate: parseFloat(rate.toFixed(3)),
      //   });
      // }
    }
  }

  return result;
};

export const getChartsVegetationMetrics = ({
  metrics,
  metricsCompared = [],
  period,
  sensorCompared,
}: ChartsMetricsArgs<VegetationMetric>) => {
  if (!metrics) return null;

  const data = getVegetationMetrics(metrics, period);

  let metricsList = {
    leafNdvi: data && data.leafNdvi ? data.leafNdvi.toReversed() : [],
    soilNdvi: data && data.soilNdvi ? data.soilNdvi.toReversed() : [],
  };

  let dataCompared: VegetationResult | undefined;

  if (metricsCompared && sensorCompared) {
    dataCompared = getVegetationMetrics(metricsCompared, period);
    metricsList = {
      ...metricsList,
      leafNdvi: MergeComparedSeries(
        metricsList.leafNdvi,
        dataCompared?.leafNdvi ? dataCompared.leafNdvi.toReversed() : [],
      ),
      soilNdvi: MergeComparedSeries(
        metricsList.soilNdvi,
        dataCompared?.soilNdvi ? dataCompared.soilNdvi.toReversed() : [],
      ),
    };
  }

  return metricsList;
};

export const getChartsNdviMetrics = ({
  metrics,
  metricsCompared = [],
  period,
  sensorCompared,
}: ChartsMetricsArgs<NdviGddMetric>) => {
  if (!metrics) return null;

  const data = getNdviGddMetrics(metrics, period);

  let metricsList = {
    ndvi: data && data.ndvi ? data.ndvi.toReversed() : [],
    gdd: data && data.gdd ? data.gdd.toReversed() : [],
    bbch: data && data.bbch ? data.bbch.toReversed() : [],
    leafArea: data && data.leafArea ? data.leafArea.toReversed() : [],
  };

  let dataCompared: NdviGddResult | undefined;

  // compared sensor
  if (metricsCompared && sensorCompared) {
    dataCompared = getNdviGddMetrics(metricsCompared, period);

    // merging selected and compared arrays
    metricsList = {
      ...metricsList,
      ndvi: MergeComparedSeries(metricsList.ndvi, dataCompared?.ndvi ? dataCompared.ndvi.toReversed() : []),
      gdd: MergeComparedSeries(metricsList.gdd, dataCompared?.gdd ? dataCompared.gdd.toReversed() : []),
      bbch: MergeComparedSeries(metricsList.bbch, dataCompared?.bbch ? dataCompared.bbch.toReversed() : []),
      leafArea: MergeComparedSeries(
        metricsList.leafArea,
        dataCompared?.leafArea ? dataCompared.leafArea.toReversed() : [],
      ),
    };
  }

  return metricsList;
};

export const getChartsAirMetrics = ({
  metrics,
  metricsCompared = [],
  period,
  sensorCompared,
}: ChartsMetricsArgs<AirMetric>) => {
  if (!metrics) return null;

  const data = getAirMetrics(metrics, period);

  let metricsList = {
    temperature: data && data.temperature ? data.temperature.toReversed() : [],
    humidity: data && data.moisture ? data.moisture.toReversed() : [],
    dew: data && data.dewPoint ? data.dewPoint.toReversed() : [],
  };

  let dataCompared: AirMetricsResult | undefined;

  // compared sensor
  if (metricsCompared && sensorCompared) {
    dataCompared = getAirMetrics(metricsCompared, period);

    // merging selected and compared arrays
    metricsList = {
      ...metricsList,
      temperature: MergeComparedSeries(
        metricsList.temperature,
        dataCompared && dataCompared.temperature ? dataCompared.temperature.toReversed() : [],
      ),
      humidity: MergeComparedSeries(
        metricsList.humidity,
        dataCompared?.moisture ? dataCompared.moisture.toReversed() : [],
      ),
      dew: MergeComparedSeries(metricsList.dew, dataCompared?.dewPoint ? dataCompared.dewPoint.toReversed() : []),
    };
  }

  return metricsList;
};

export const getChartsSoilMetrics = ({ metrics }: { metrics: Nullable<SoilMetric[]> }) => {
  if (!metrics || metrics.length === 0) return;

  const count = metrics[0].soil_moisture.length;
  const selectedDeepDefault = Array.from({ length: count }, (_, index) => ({
    value: ChartDeepOptions[index]?.value,
    color: ChartDeepOptions[index]?.color,
  })).filter((item) => item.value !== undefined);

  const soilMoisture = getSoilMoistureChartData(metrics);
  const moisture = {
    data: soilMoisture.data,
    dataByDays: soilMoisture.dataByDays,
    count: metrics[0].soil_moisture.length,
    selectedDeep: selectedDeepDefault,
  };

  const soilTemperature = getSoilTemperatureChartData(metrics);
  const temperature = {
    data: soilTemperature.data,
    dataByDays: soilTemperature.dataByDays,
    count: metrics[0].soil_temperature.length,
    selectedDeep: selectedDeepDefault,
  };

  const salinity = {
    ...getSoilSalinityChartData(metrics),
    selectedDeep: selectedDeepDefault,
  };

  const rain = {
    data: getRainChartData(metrics),
  };

  return {
    soilHumidity: moisture ?? [],
    soilTemperature: temperature ?? [],
    salinity: salinity ?? [],
    rain: rain ?? [],
  };
};

/** ==== CSV format ==== */
const generateCSV = (data: Record<string, unknown>[], keys?: string): string => {
  const headers = keys ? keys : data.length ? Object.keys(data[0]).join(',') : '';
  const rows = data.map((row) => Object.values(row).join(',')).join('\n');
  return `${headers}\n${rows}`;
};
/** Download CSV file */
export const downloadCSV = async ({
  data,
  keys,
  fileName,
}: {
  data: Record<string, unknown>[];
  keys?: string;
  fileName: string;
}): Promise<void> => {
  const csvContent = generateCSV(data, keys);

  const isMobilePlatform = Capacitor.isNativePlatform();

  if (isMobilePlatform) {
    const result = await Filesystem.writeFile({
      path: `${fileName}.csv`,
      data: csvContent,
      directory: Directory.Documents,
      encoding: Encoding.UTF8,
    });

    await Share.share({
      title: 'Download CSV',
      url: result.uri,
    });
  } else {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};
